import { FocusEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building, DeviceClass, DeviceFamily, DeviceTypeId, DeviceType, DeviceConfigId, DeviceProperties } from 'shared/models';
import { useDeviceConfigs, useForm, useSession } from 'hooks';
import { ErrorMessage } from 'ui/shared/messages';
import { AddDisplayFormFields } from "./addDisplayFormFields/AddDisplayFormFields";
import { AddModemFormFields } from "./addModemFormFields/AddModemFormFields";
import { AddAtaFormFields } from "./addAtaFormFields/AddAtaFormFields";
import { AddSimCardFormFields } from "./addSimCardFormFields/AddSimCardFormFields";
import { DeviceClassCard } from "../deviceClassCard/DeviceClassCard";
import displayIcon from "assets/images/deviceTypes/m1-icon-display.svg";
import modemIcon from "assets/images/deviceTypes/m1-icon-modem.svg";
import ataIcon from "assets/images/deviceTypes/m1-icon-ata.svg";
import simCardIcon from "assets/images/deviceTypes/m1-icon-simcard.svg";
import { Permission } from "shared/permissions";
import { TextField } from "ui/shared/controls";

require('./AddDevicesForm.css');

const nullValue = 'none';

export type Props = {
    busy: boolean;
    building: Building;
    deviceTypes: Record<DeviceTypeId, DeviceType>;
    errorMessage: string;
    onClickCreate(data: FormData): void;
    onClickCancel(): void;
};

export type FormData = {
    displayName: string;
    deviceFamily: DeviceFamily | null;
    deviceTypeId: DeviceTypeId | null;
    deviceClass: DeviceClass;
    elevatorId: string;
    groupName: string | null;
    deviceConfigId: DeviceConfigId | null;
    quantity: number;
    pi: boolean;
    properties: DeviceProperties;
    macAddress: string | null;
};

const initialData: FormData = {
    displayName: '',
    deviceFamily: DeviceFamily.MatisseR5,
    deviceTypeId: null,
    deviceClass: DeviceClass.Display,
    elevatorId: '',
    groupName: '',
    deviceConfigId: null,
    quantity: 1,
    pi: true,
    properties: {},
    macAddress: null,
};

export const AddDevicesForm = ({ busy, building, deviceTypes, errorMessage, onClickCreate, onClickCancel }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();

    const { data, setData, handleChange, handleSubmit } = useForm('new-device', initialData, onClickCreate, { nullValue });
    const { loading: isLoadingDeviceConfigs } = useDeviceConfigs(building.id);

    const handleSelectDeviceClass = useCallback((deviceClass: DeviceClass) => {
        setData(data => ({ ...data, deviceClass }));
    }, [ setData ]);

    const isStandardUser = hasPermission(null, Permission.DebugRead);

    let disableSubmitButton = busy || isLoadingDeviceConfigs;
    if (data.deviceClass === DeviceClass.Modem) {
        disableSubmitButton = disableSubmitButton || !data.deviceTypeId || !data.properties.serialNumber;
    }
    else if (data.deviceClass === DeviceClass.ATA) {
        disableSubmitButton = disableSubmitButton || !data.deviceTypeId || !data.properties.serialNumber || !data.macAddress;
    }
    else if (data.deviceClass === DeviceClass.SIMCard) {
        disableSubmitButton = disableSubmitButton || !data.deviceTypeId || !data.properties.imei;
    }
    else {
        disableSubmitButton = disableSubmitButton || !data.deviceTypeId;
    }

    //
    // Highlight the entire quantity when the control gains focus
    //
    const handleFocusQuantity = useCallback((e: FocusEvent<HTMLInputElement>) => {
        const { currentTarget } = e;
        currentTarget.select();
    }, []);
        
    const deviceClassOptions = [
        { type: DeviceClass.Display, imageSrc: displayIcon, displayName: 'Display' },
        { type: DeviceClass.ATA, imageSrc: ataIcon, displayName: 'ATA' },
        { type: DeviceClass.Modem, imageSrc: modemIcon, displayName: 'Modem' },
        { type: DeviceClass.SIMCard, imageSrc: simCardIcon, displayName: 'SIM Card' },
    ];

    //
    // Reset the device type and device config whenever the family changes
    //
    useEffect(() => {
        setData(data => ({
            ...data,
            deviceTypeId: null,
            deviceConfigId: null,
        }));
    }, [ data.deviceFamily, setData ]);

    useEffect(() => {
        // Let user enter name for the Display device... all others will just
        // default to the name of the device class (and user cannot edit)
        const displayName =
            data.deviceClass === DeviceClass.Display
                ? ''
                : deviceClassOptions.find(o => o.type === data.deviceClass)!.displayName;

        setData(data => ({
            ...data,
            deviceTypeId: null,
            deviceFamily: null,
            displayName,
        }));
    }, [ data.deviceClass, setData ]);

    return (
        <form
            id="add-devices-form"
            onSubmit={ handleSubmit }
            className={ `ui form${errorMessage ? ' error' : ''}${busy ? ' loading' : ''}` }
        >
            { isStandardUser &&
                <div className="device-classes-container">
                    {
                        deviceClassOptions.map((dc, index) => (
                            <DeviceClassCard
                                key={ index }
                                imageSrc={ dc.imageSrc }
                                displayName={ dc.displayName }
                                isActive={ dc.type === data.deviceClass }
                                onSelect={ () => handleSelectDeviceClass(dc.type) }
                            />
                        ))
                    }
                </div>
            }
            
            { data.deviceClass === DeviceClass.Display &&
                <AddDisplayFormFields
                    building={ building }
                    deviceTypes={ deviceTypes }
                    onChange={ handleChange }
                    data={ data }
                    setData={ setData }
                />
            }

            { data.deviceClass === DeviceClass.Modem &&
                <AddModemFormFields
                    deviceTypes={ deviceTypes }
                    data={ data }
                    onChange={ handleChange }
                />
            }

            { data.deviceClass === DeviceClass.ATA &&
                <AddAtaFormFields
                    deviceTypes={ deviceTypes }
                    data={ data }
                    onChange={ handleChange }
                />
            }

            { data.deviceClass === DeviceClass.SIMCard &&
                <AddSimCardFormFields
                    deviceTypes={ deviceTypes }
                    data={ data }
                    onChange={ handleChange }
                />
            }

            <div className="buttons">
                <button
                    className="ui primary submit button"
                    type="submit"
                    disabled={ disableSubmitButton }
                >
                    { t('COMMAND.CREATE_DEVICE') }
                </button>
                <button
                    className="ui button"
                    type="button"
                    disabled={ busy }
                    onClick={ onClickCancel }
                >
                    { t('COMMAND.CANCEL') }
                </button>

                { data.deviceClass === DeviceClass.Display &&
                    <TextField
                        id="add-devices-form_quantity"
                        className="qty"
                        name="quantity"
                        labelKey="DEVICES.EDIT_DIALOG.FIELD.QUANTITY"
                        maxLength={ 2 }
                        required={ true }
                        autoComplete="off"
                        value={ data.quantity }
                        onChange={ handleChange }
                        onFocus={ handleFocusQuantity }
                    />
                }
            </div>

            <ErrorMessage
                className="error"
                message={ errorMessage }
            />
        </form>
    );
};